import {PdsWidget} from './pds-widget';
import {Validator} from 'common-utils/interfaces';
import { pdsValidationRulesEngine } from './pds-validation-rules-engine';
import { pdsI18n } from './pds-i18n';
import { pdsUtils } from './pds-utils';
import { pdsLogger } from './pds-logger';
import {ValidationResult} from 'common-utils/dist/interfaces';
let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('input-widgets'), 'pds-abstract-input-widget.ts');

//Doing a querySelectorAll on this string will provide all input widgets on the page.
export let allInputTagsSelector = 'pds-number-widget,pds-date-widget,pds-select-widget,pds-text-widget,pds-time-widget,pds-rich-text-editor,pds-file-picker-widget,pds-radio-button-widget,pds-checkbox-widget,pds-textarea-widget,pds-multi-select-widget';
const tabKey = 9;

export interface InputSelectorResult {
  value: string | undefined | null;
  modelValue: any;
}

export abstract class PdsAbstractInputWidget extends PdsWidget {
  name: string;

  errorMessage: string;
  isValid: boolean;
  invalidRule: string | undefined;
  isAttached: boolean;
  isBadgeElevated: boolean;

  //Validation rules
  dataIsreadonly: string;
  dataIsrequired: string;
  dataCustomvalidate: string;

  //Show additional validation indicator if true. Used in the central layout views for login and account forms
  dataUseValidIndicator: 'false' | 'true' | 'loading';

  //Validation Options
  dataHideUnelevatedBadge: string;
  dataDisableInvalidBadgeElevation: string;

  //Visibility Flags
  isBadgeHidden: boolean;
  showTooltip: boolean;
  dataUseAlternateColorScheme: string;
  //right now the only things that make that happen are focusLost and pds.validation.validateElements
  showValidIndicator: boolean; // is false until field has focusLost.
  //Text labels
  dataLabelText: string;
  dataBadgeText: string;
  dataHelperText: string;
  dataFieldHelpText: string;
  dataPlaceholderText: string;
  dataTooltipText: string;
  disabled: string;

  //toggle to show the labels.

  dataShowLabel: string;

  originalBadgeText: string;

  validators: Array<Validator>;

  actionIcon: string;

  // each input widget must have a reference to its own error field, so that we can
  // add the hidden class to dynamically show and hide the error.

  myErrorField: HTMLElement;

  // this variable is initially set to false and corresponds to the on-blur property of an input field
  // on-blur is tied to a function that (on-blur="functionName") fires a FocusEvent.
  // inside that function, we set this value to true only if there are any validation errors.
  errorPresentWhenBlurOccurred = false;

  blurInterceptor: Function;

  // this variable will allow for the data-label-text attribute error message to be hidden from the console
  // this has been specifically added for registration as they already have a way to show these errors
  disableErrorMessageLabelText: boolean | undefined;

  // this variable will allow for the option name/value warning messsages to be hidden from the console
  // this has been specifically added for registration as they already have a way to show these warnings
  disableWarningMessageDuplicateOption: boolean | undefined;

  attributeChanged(): void {
    //this is a polymer hook
    log('attributeChange() called');
    this.generateValidationRules();
  }

  ready(): void {     //this is a polymer hook
    log('ready() called');
    super.ready();

    //Initialize all of the member variables here because it doesn't
    //work right to do that in the place where the variables are defined.
    this.errorMessage = '';
    this.isValid = true;
    this.invalidRule = '';
    this.isAttached = false;
    this.isBadgeElevated = false;

    this.isBadgeHidden = true;
    this.showTooltip = false;
    this.showValidIndicator = false;

    this.originalBadgeText = '';
    this.messageKeysUpdated();
    this.generateValidationRules();
    // grab a reference to both the input field and the error field.
    this.myErrorField = <HTMLElement>this.querySelector('.pds-validation');
    this.showErrorMessage(false);

     /**
      * Waiting for angular to finish its cycle before checking the data label text
      */
    setTimeout(() => {
      if (!this.disableErrorMessageLabelText && (this.dataLabelText == null || this.dataLabelText.length === 0)) {
        console.error('There needs to be a data-label-text attribute to this input widget', this);
      }
    });
  }

  /**
   * Gets the input element. Defaults to using query selector to find the first child input.
   */
  inputSelector(): InputSelectorResult {
    return this.querySelector('input');
  }

  inputChange(): void {
    log('inputChange() called');
    this.validateWidget();
    this.setValue(this.value);
    (<any>this).dispatchEvent(new Event('modelValueChange', {bubbles: true}));
  }

  /**
   * When the user enters data into this field and the focus is lost, the widget calls this function on blur and fires
   * a focus event. We check if there is an error message, if there is an error message,
   * then we start allowing the error messages to be shown
   */
  focusLost(event: any): void {
    log('focus lost');
    if (this.blurInterceptor != null) {
      this.blurInterceptor(this);
    }
    this.validateWidget(true, true);
    this.dispatchEvent(new Event('blur'));
  }

  /**
   * This allows the user to focus on an error message if one is diisplayed in the input widget
   * if there is an error message (errorMessage.length > 0) - visibility is true
   * if there is not an error message (errorMessage.length == 0) - visibility is false
   *
   * Note: this is bad practice but to ensure screen readers pick this up.
   * @param visibility
   */
  showErrorMessage(makeVisible: boolean): void {
    log(`showErrorMessage(${makeVisible}) called. this.errorMessage: ${this.errorMessage}`);
    if (this.myErrorField == null) {
      return;
    }

    //We hide it if there is no error message or we are making it invisible (not visible).
    pdsUtils.toggleClass(this.myErrorField, 'sr-only', this.errorMessage.length === 0 || !makeVisible);
    this.myErrorField.textContent = this.errorMessage;
  }

  get value(): any {
    let result = this.inputSelector();
    if (result !=  null) {
      log(`get value() called - value: ${result.value}`);
      return result.value;
    }
    log('get value() called - inputSelector not there yet');
  }

  set value(value) {
    log(`set value(${value}) called`);
    this.setValue(value);
    this.setReadonlyText();
  }

  get modelValue(): any {
    let result = this.inputSelector();

    if (result != null) {
      log(`get modelValue() called - value: ${result.modelValue}`);
      //In the Angular 2+ event listener, we grab event.target.modelValue
      return result.modelValue;
    }

    log('get modelValue() called - inputSelector not there yet');
  }

  set modelValue(value) {
    log(`set modelValue(${value})`);
    let modelValueChanged = this.setModelValue(value);
    if (modelValueChanged) {
      //only show the validation message IF an error message is already showing.
      let showValidationResult = this.isErrorMessageShowing();
      this.validateWidget(showValidationResult);
    }
    this.setReadonlyText();
  }

  /**
   * Returns true if an error message string is currently showing up in the UI.  If a required badge is elevated, then
   * this would not show up.
   * @returns {boolean}
   */
  isErrorMessageShowing(): boolean {
    return this.myErrorField != null && !this.myErrorField.classList.contains('sr-only');
  }

  /**
   * Validates the widget and sets the isValid flag.
   * If the widget is valid, then it will always remove the error message.
   * Optionally, it can make the error message show up if the widget is invalid.
   * @param showValidationResult: True - will make the error message show up.
   *                              False - will make sure the error message will not show up.
   *                              undefined - will show the error message if this.errorPresentWhenBlurOccurred === true
   * @param fromBlur: True - means that you called this method from the blur handler.  Otherwise defaults to false.
   * @returns {boolean} - True means the widget is valid. False means the widget is invalid.
   */
  validateWidget(showValidationResult?: boolean, fromBlur?: boolean): boolean {
    log(`validateWidget(${showValidationResult}, ${fromBlur}) called.`);
    //default fromBlur to false.
    if (fromBlur == null) {
      fromBlur = false;
    }

    let validationResult = this.runValidationEngine();
    this.isValid = validationResult.isValid;
    if (showValidationResult === true || fromBlur) {
      this.showValidIndicator = true;
    }
    if (this.isValid) {
      this.setError('');
      this.invalidRule = '';
    } else {
      this.invalidRule = validationResult.invalidRule;
      if (validationResult.invalidRule === 'isRequired') {
        this.setError('');
      } else {
        this.setError(validationResult.errorMessage);
      }
    }
    this.setUpBadge();

    //At this point we know if the widget is valid and what the error message should be.
    //So we can decide whether to show the error messages or not.
    let showErrorMessages = false;

    //It's gotta be invalid and have an actual error message in order to have a chance at showing an error message.
    if (!this.isValid && this.errorMessage.length > 0) {
      if (fromBlur && !this.errorPresentWhenBlurOccurred) {
        //This flag should only get set when this is called through the focusLost function.
        this.errorPresentWhenBlurOccurred = true;
      }

      if (showValidationResult == null) {
        showErrorMessages = this.errorPresentWhenBlurOccurred;
      } else {
        showErrorMessages = showValidationResult;
      }
    }

    //We always update the error message span each time we validate.
    //This ensures that it is always up to date.
    this.showErrorMessage(showErrorMessages);

    return this.isValid;
  }

  /**
   * This was refactored out to support the pds-mutlti-select-widget's ability to override the validateWidget function.
   */
  protected runValidationEngine(): ValidationResult {
    return pdsValidationRulesEngine.validate(this.value, this.validators);
  }

  setError(errorMsg): void {
    if (errorMsg != null && errorMsg.trim() !== '') {
      this.errorMessage = errorMsg;
    } else {
      this.errorMessage = '';
    }
  }

  abstract setValue(value: any, skip?: boolean): void;

  abstract setModelValue(value: any, skip?: boolean): void;

  abstract generateValidationRules(): void;

  attached(): void {  //this is a polymer hook
    log('attached() called');
    let valueAttr = (<any>this).getAttribute('value');
    if (valueAttr != null) {
      (<any>this).removeAttribute('value');
      this.value = valueAttr;
    }
    this.isAttached = true;
    //Initialize the badge
    this.setUpBadge();
  }

  setReadonlyText(): void {
    if (this.dataIsreadonly != null && this.value != null) {
      let readOnlyEle = this.querySelector('.pds-readonly-data');
      if (readOnlyEle == null) {
        return;
      }

      if (this.dataIsreadonly === 'true') {
        readOnlyEle.innerText = this.value;
      } else {
        readOnlyEle.innerText = '';
      }
    }
  }

  setUpBadge(): void {
    log('setUpBadge() called.  isValid: ' + this.isValid);
    if (this.isAttached) {  // if attached hasn't fired yet this won't work
      //calculate parameters
      let isElevated = !this.isValid && this.dataDisableInvalidBadgeElevation !== 'true'
        && this.invalidRule === 'isRequired' && this.dataIsrequired === 'true';
      let isBadgeTextConfigured = (this.hasAttribute('data-badge-text')
        && this.dataBadgeText != null && this.dataBadgeText.length > 0);
      let hidingUnelevated = this.dataHideUnelevatedBadge === 'true' && !isElevated;
      let isRequired = this.dataIsrequired === 'true';

      //set badge elevation
      this.isBadgeElevated = isElevated;

      //set original badge text
      if (this.dataBadgeText != null && this.dataBadgeText != null
        && this.dataBadgeText.length > 0 && this.originalBadgeText === '') {
        this.originalBadgeText = this.dataBadgeText;
      }

      //set badge text
      if (this.dataIsrequired === 'true') {
        this.dataBadgeText = pdsI18n.getText('pds|common|Required');
      } else if (!isBadgeTextConfigured) {
        this.originalBadgeText = '';
      } else if (isBadgeTextConfigured) {
        this.dataBadgeText = this.originalBadgeText;
      }

      //set badge visibility
      if (!hidingUnelevated && (isRequired || isBadgeTextConfigured)) {
        this.isBadgeHidden = false;
      } else {
        this.isBadgeHidden = true;
      }
    }
  }

  /**
   * This gets called when the input fields need to be reset.
   * This forces the input field to behave as if the user initially arrived to that field.
   */

  resetInputWidgetState(): void {
    log('resetInputWidgetState() called');
    this.errorPresentWhenBlurOccurred = false;
    this.showErrorMessage(false);
    this.isValid = true;
  }

  showLabel(dataShowLabel: string, dataLabelText: string): boolean {
    return (dataLabelText != null && dataLabelText.length > 0 && dataShowLabel !== 'false');
  }

  showFieldHelp(dataFieldHelpText: string): boolean {
    return (this.dataTooltipText != null && this.dataTooltipText.length > 0);
  }

  showHelperText(dataHelperText: string): boolean {
    return (this.dataHelperText != null && this.dataHelperText.length > 0);
  }

  // Validation is not triggered until the user interacts with the field
  canShowIndicator(useValidIndicator: string, showValidIndicator: boolean): boolean {
    return useValidIndicator === 'true' && showValidIndicator;
  }
  //The validation that occurs within the validation widgets is SYNCHRONOUS. The loading icon gets triggered by the external controller, which is responsible for setting this.dataUseValidIndicator to "true" when the REST call finishes.
  canShowLoadingIndicator(useValidIndicator: string, showValidIndicator: boolean): boolean {
    return useValidIndicator === 'loading' && showValidIndicator;
  }

  actionButtonClickHandler(event: Event): void {
    this.fire('actionButtonEvent', event);
  }

  //Fires the input event so everyone who is listening will know (e.g. pds-form angular directive)
  dispatchInputEvent(inputElement: HTMLElement): void {
    let inputEvent: any = new Event('input', { bubbles: true });
    inputElement.dispatchEvent(inputEvent);
  }

  isDisabled(): string | null {
    return this.disabled === 'true' || this.disabled === 'disabled' ? 'disabled' : null;
  }

  getDisabledTextIfDisabled(disabled?: 'true' | 'false' | 'disabled'): string {
    if (this.isDisabled()) {
      return pdsI18n.getText('pds|common|Disabled field ');
    } else {
      return '';
    }
  }
}
